import type {
  AdminAiRatings,
  AdminAiRatingsSurface,
  AdminAiRatingTally,
  AiRatingSurfaceName,
} from '@steado/admin-contract/ai-ratings';
import type { AdminAiWeeklyReviews } from '@steado/admin-contract/ai-ops';
import type { ReactNode } from 'react';

import { DataTable, type TableColumn, type TableRow } from '@/components/table/DataTable';
import { TextCell } from '@/components/table/cells';
import { formatRate } from '@/lib/format-spend';
import { humanise } from '@/lib/format';

import { fallbackRateOf } from './weekly-review-rate';

interface RatingsPanelProps {
  readonly ratings: AdminAiRatings;
  readonly weeklyReviews: AdminAiWeeklyReviews | undefined;
}

interface SurfaceCopy {
  readonly label: string;
  readonly exposureNote: string | null;
}

const SURFACE_COPY: Readonly<Record<AiRatingSurfaceName, SurfaceCopy>> = {
  coach_message: {
    label: 'Coach replies',
    exposureNote:
      'Shown for coach replies counts every final coach reply that is not a safety refusal or a card receipt. ' +
      'That includes replies sent after the daily allowance ran out and lines the app words itself, ' +
      'because no column tells them apart from model replies. Treat the share of coach replies rated as a floor.',
  },
  weekly_review: { label: 'Weekly reviews', exposureNote: null },
};

const UNKNOWN_MODEL_WARN_SHARE = 0.5;

const SURFACE_COLUMNS: readonly TableColumn[] = [
  { key: 'surface', label: 'Surface', width: '1.5fr' },
  { key: 'shown', label: 'Shown', width: '0.7fr', align: 'right' },
  { key: 'rated', label: 'Rated', width: '0.9fr', align: 'right' },
  { key: 'up', label: 'Thumbs up', width: '0.7fr', align: 'right' },
  { key: 'down', label: 'Thumbs down', width: '0.8fr', align: 'right' },
  { key: 'approval', label: 'Approval', width: '0.7fr', align: 'right' },
  { key: 'unknown', label: 'Model unknown', width: '1fr', align: 'right' },
];

const SPLIT_COLUMNS: readonly TableColumn[] = [
  { key: 'split', label: 'Split', width: '1.6fr' },
  { key: 'rated', label: 'Rated', width: '0.7fr', align: 'right' },
  { key: 'up', label: 'Thumbs up', width: '0.7fr', align: 'right' },
  { key: 'down', label: 'Thumbs down', width: '0.8fr', align: 'right' },
  { key: 'approval', label: 'Approval', width: '0.7fr', align: 'right' },
  { key: 'share', label: 'Share of rated', width: '0.9fr', align: 'right' },
];

function share(part: number, whole: number): number | null {
  return whole === 0 ? null : part / whole;
}

function Count({ value }: { readonly value: number }) {
  return <TextCell value={value.toLocaleString('en-IN')} mono align="right" />;
}

function splitCells(key: string, tally: AdminAiRatingTally, whole: number): ReactNode[] {
  return [
    <Count key={`${key}-rated`} value={tally.rated} />,
    <Count key={`${key}-up`} value={tally.up} />,
    <Count key={`${key}-down`} value={tally.down} />,
    <TextCell key={`${key}-approval`} value={formatRate(share(tally.up, tally.rated))} mono align="right" />,
    <TextCell key={`${key}-share`} value={formatRate(share(tally.rated, whole))} mono align="right" />,
  ];
}

function surfaceRow(
  surface: AdminAiRatingsSurface,
  weeklyReviews: AdminAiWeeklyReviews | undefined,
): TableRow {
  const copy = SURFACE_COPY[surface.surface];
  const fallbackRate =
    surface.surface === 'weekly_review' && weeklyReviews !== undefined ? fallbackRateOf(weeklyReviews) : null;
  const unknownShare = share(surface.modelUnknown.rated, surface.rated);

  return {
    id: surface.surface,
    cells: [
      <TextCell
        key="surface"
        value={copy.label}
        sub={fallbackRate === null ? null : `${formatRate(fallbackRate)} fell back to code`}
        weight="medium"
      />,
      <Count key="shown" value={surface.exposures} />,
      <TextCell
        key="rated"
        value={surface.rated.toLocaleString('en-IN')}
        sub={surface.ratedShare === null ? null : `${formatRate(surface.ratedShare)} of shown`}
        mono
        align="right"
      />,
      <Count key="up" value={surface.up} />,
      <Count key="down" value={surface.down} />,
      <TextCell key="approval" value={formatRate(surface.approvalRate)} mono align="right" weight="medium" />,
      <TextCell
        key="unknown"
        value={formatRate(unknownShare)}
        sub={`${String(surface.modelUnknown.rated)} of ${String(surface.rated)}`}
        mono
        align="right"
        {...(unknownShare !== null && unknownShare > UNKNOWN_MODEL_WARN_SHARE
          ? { tone: 'warn' as const }
          : {})}
      />,
    ],
  };
}

function modelRows(surface: AdminAiRatingsSurface): TableRow[] {
  const label = SURFACE_COPY[surface.surface].label;
  const known = surface.byModel.map((entry) => ({
    id: `${surface.surface}-${entry.model}`,
    model: entry.model,
    tally: entry,
  }));
  const rows = [
    ...known,
    ...(surface.modelUnknown.rated === 0
      ? []
      : [{ id: `${surface.surface}-unknown`, model: null, tally: surface.modelUnknown }]),
  ];

  return rows.map((row) => ({
    id: row.id,
    cells: [
      <TextCell
        key="split"
        value={row.model ?? 'Model unknown'}
        sub={label}
        {...(row.model === null ? { tone: 'muted' as const } : {})}
      />,
      ...splitCells(row.id, row.tally, surface.rated),
    ],
  }));
}

function proseSourceRows(surface: AdminAiRatingsSurface): TableRow[] {
  if (surface.surface !== 'weekly_review') return [];
  return surface.byProseSource.map((entry) => {
    const id = `prose-${entry.proseSource ?? 'unrecorded'}`;
    return {
      id,
      cells: [
        <TextCell key="split" value={humanise(entry.proseSource ?? 'not recorded')} />,
        ...splitCells(id, entry, surface.rated),
      ],
    };
  });
}

function Notes({ children }: { readonly children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5 border-t border-border bg-secondary/40 px-5 py-3 text-xs leading-[1.55] text-muted-foreground">
      {children}
    </div>
  );
}

export function RatingsPanel({ ratings, weeklyReviews }: RatingsPanelProps) {
  const notes = ratings.surfaces.flatMap((surface) => {
    const note = SURFACE_COPY[surface.surface].exposureNote;
    return note === null ? [] : [<p key={surface.surface}>{note}</p>];
  });
  const models = ratings.surfaces.flatMap(modelRows);
  const prose = ratings.surfaces.flatMap(proseSourceRows);
  const windowLabel = `${String(ratings.windowDays)} days`;

  return (
    <div className="flex flex-col gap-3">
      <DataTable
        caption="Ratings by surface"
        title="Ratings"
        count={windowLabel}
        columns={SURFACE_COLUMNS}
        rows={ratings.surfaces.map((surface) => surfaceRow(surface, weeklyReviews))}
        isLoading={false}
        emptyTitle="Nothing to rate yet"
        emptyBody="No surface has anything to show."
        footer={
          <Notes>
            <p>
              A rating counts in the window its reply or review was written in. The model is matched by time
              against the model calls on record, so it is blank whenever more than one model, or none, served
              the user around then.
            </p>
            {notes}
          </Notes>
        }
      />

      <DataTable
        caption="Ratings by model"
        title="By model"
        count={windowLabel}
        columns={SPLIT_COLUMNS}
        rows={models}
        isLoading={false}
        emptyTitle="No ratings in this window"
        emptyBody="Thumbs from the app will show here, split by the model that wrote the reply."
      />
      <DataTable
        caption="Weekly reviews by who wrote the prose"
        title="Weekly reviews by prose"
        count={windowLabel}
        columns={SPLIT_COLUMNS.map((column) =>
          column.key === 'split' ? { ...column, label: 'Written by' } : column,
        )}
        rows={prose}
        isLoading={false}
        emptyTitle="No reviews rated in this window"
        emptyBody="A review rated here shows whether the model or the code wrote its prose."
      />
    </div>
  );
}
